import { canv } from "@/canv/global";
import { generateUID } from "@/canv/utils";
import type { ElementBase } from "@/elementBase";
import type { CardWidget } from "@/elements/card";
import type { ImageWidget } from "@/elements/image";
import type { ShapeWidget } from "@/elements/shape";
import type { StickerWidget } from "@/elements/sticker";
import type { TextWidgetV2 } from "@/elements/text/textV2";
import type { BuildEventStructure } from "@/eventManager/events/types";
import { t } from "@/i18n";

type VoteCandidateWidget = ShapeWidget | ImageWidget | TextWidgetV2 | StickerWidget | CardWidget;

const isVoteCandidateWidget = (widget: ElementBase): widget is VoteCandidateWidget => {
    switch (widget.constructorName) {
        case "shape":
        case "image":
        case "textV2":
        case "sticker":
        case "card":
            return true;

        default:
            return false;
    }
};

export const createVoteBuildEvent = ({
    timeCount,
    votesCount,
    oneVotePerItem,
}): BuildEventStructure[] => {
    return [
        {
            type: "build",
            action: "create",
            entities: [createEntityForVoteBuildEvent({ timeCount, votesCount, oneVotePerItem })],
        },
    ];
};

const createEntityForVoteBuildEvent = ({ timeCount, votesCount, oneVotePerItem }) => {
    return {
        entityType: "vote",
        UID: generateUID(),
        text: t("vote.defaultName", {
            number: canv.canvasController.pastVotes.length + 1,
        }),
        start: new Date(),
        duration: timeCount * 60,
        votePerAccount: votesCount,
        oneVotePerItem,
        isResolved: false,
        creatorToken: canv.getInitDataProp("userToken"),
        creatorName: canv.getInitDataProp("userName"),
        children: createEntityChildForVoteBuildEvent(),
        participants: [
            {
                token: canv.getInitDataProp("userToken"),
                accountName: canv.getInitDataProp("userName"),
            },
        ],
    };
};

const createEntityChildForVoteBuildEvent = () => {
    if (canv.appInterface.voteWindow.screen === null) {
        throw new Error("Ошибка: не найдена область голосования");
    }

    const { candidates, selectedGroupUIDs } = canv.appInterface.voteWindow.screen;
    const result = {};

    for (const groupUID of selectedGroupUIDs) {
        const group = candidates.get(groupUID);

        // выбранная группа есть в кандидатах
        if (group) {
            const widget = group.onlyChild();

            if (!widget) {
                throw new Error("Ошибка: группа не itemWrapper");
            }

            if (!isVoteCandidateWidget(widget)) {
                throw new Error("Ошибка: виджет не подходит для голосования");
            }

            result[widget.UID] = {
                linkToOriginalUID: widget.UID,
                widgetData: getWidgetData(widget),
                votes: [],
            };
        }
    }

    return result;
};

const getWidgetData = (widget: VoteCandidateWidget) => {
    if (widget.constructorName === "shape") {
        return {
            UID: widget.UID,
            entityType: "shape",
            shapeType: widget.mode,
            color: widget.color,
            borderColor: widget.borderColor,
            text: widget.textPart.getText(),
            x: widget.startX,
            y: widget.startY,
        };
    }

    if (widget.constructorName === "image") {
        return {
            UID: widget.UID,
            entityType: "media",
            imageUrl: widget.src,
            text: "",
            width: widget.width,
            height: widget.height,
        };
    }

    if (widget.constructorName === "textV2") {
        return {
            UID: widget.UID,
            entityType: "text",
            text: widget.getText(),
            font: widget.getData().fontFamily,
            fontSize: widget.fontSize,
        };
    }

    if (widget.constructorName === "sticker") {
        return {
            UID: widget.UID,
            entityType: "sticker",
            color: widget.color,
            text: widget.textPart.getText(),
            x: widget.startX,
            y: widget.startY,
        };
    }

    if (widget.constructorName === "card") {
        return {
            UID: widget.UID,
            entityType: "card",
            text: widget.textTitle?.getText() || "",
            progress: widget.progress,
            x: widget.startX,
            y: widget.startY,
        };
    }
};
